import {TranslateService} from '@ngx-translate/core';
import {ServerTranslateService} from '@services/server-translate.service';
import {weatherLegendTitleKey} from '../components/weather-legend/legend-match.util';
import {isYearlyWeatherLayerName, stripWeatherLayerPeriod} from './weather-layer-name.util';

/** One row per GeoServer store name (API may return duplicates). */
export function dedupeTileLayersByGeoServerName(layers: unknown[]): any[] {
  const byName = new Map<string, any>();
  for (const layer of layers ?? []) {
    const name = (layer as {geoServerName?: string})?.geoServerName;
    if (!name || byName.has(name)) {
      continue;
    }
    byName.set(name, layer);
  }
  return [...byName.values()];
}

/** One select row per indicator title (same label must not repeat for different MMdd/YYYYMMDD). */
export function dedupeTileLayersBySelectTitleKey(layers: unknown[]): any[] {
  const byKey = new Map<string, any>();
  for (const layer of dedupeTileLayersByGeoServerName(layers)) {
    const geoServerName = (layer as {geoServerName?: string})?.geoServerName;
    const key = weatherLayerSelectTitleKey(geoServerName) ?? geoServerName ?? '';
    if (!key || !byKey.has(key)) {
      byKey.set(key, layer);
    }
  }
  return [...byKey.values()];
}

const SUM_OF_ACTIVE_TEMPERATURES = /^SumOfActiveTemperaturesBase\d+$/;

/** Dropdown title key: per-indicator names, else legend title keys (docs/WEATHER.md). */
export function weatherLayerSelectTitleKey(geoServerName: string | null | undefined): string | null {
  if (!geoServerName) {
    return null;
  }
  const indicator = stripWeatherLayerPeriod(geoServerName);
  if (!indicator) {
    return weatherLegendTitleKey(geoServerName);
  }

  if (/Deviation/i.test(geoServerName) && !isYearlyWeatherLayerName(geoServerName)) {
    return `Deviation${indicator}`;
  }

  if (SUM_OF_ACTIVE_TEMPERATURES.test(indicator)) {
    return indicator;
  }

  if (/Normal/i.test(geoServerName)) {
    return `Normalized${indicator}`;
  }

  return weatherLegendTitleKey(geoServerName);
}

/** Dropdown label: client i18n first, then server `Names`. */
export function weatherLayerSelectLabel(
  layer: {geoServerName?: string; names?: Record<string, string>},
  translate: TranslateService,
  serverTranslate: ServerTranslateService
): string {
  const titleKey = weatherLayerSelectTitleKey(layer.geoServerName);
  if (titleKey) {
    const text = translate.instant(titleKey);
    if (text && text !== titleKey) {
      return serverTranslate.formatTranslationLabel(text);
    }
  }
  const fromServer = serverTranslate.translate(layer, 'names');
  if (typeof fromServer === 'string' && serverTranslate.isValidDisplayLabel(fromServer)) {
    return serverTranslate.formatTranslationLabel(fromServer);
  }
  const legendKey = weatherLegendTitleKey(layer.geoServerName);
  if (legendKey) {
    const text = translate.instant(legendKey);
    if (text && text !== legendKey) {
      return serverTranslate.formatTranslationLabel(text);
    }
  }
  return layer.geoServerName ?? '';
}

export function mapWeatherLayerSelectOptions(
  layers: unknown[],
  translate: TranslateService,
  serverTranslate: ServerTranslateService
): any[] {
  return dedupeTileLayersBySelectTitleKey(layers).map((layer) => ({
    ...layer,
    label: weatherLayerSelectLabel(layer, translate, serverTranslate)
  }));
}
